import { yupResolver } from "@hookform/resolvers/yup";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import GroupIcon from "@mui/icons-material/Group";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DataUsageIcon from "@mui/icons-material/DataUsage";
import { Alert, Box, Button, Card, CardContent, IconButton, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Tooltip, Chip, Avatar } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import CustomInput from "../../../../ui/components/custom-input/custom-input";
import { afiliadoSempreServices } from "../../../services/afiliado-sempre.services";
import { importacionSempreServices } from "../../../services/importacion-sempre.services";
import { prestadorSempreServices } from "../../../services/prestador-sempre.services";
import "./sempre-importacion.scss";

interface ImportacionForm {
  descripcionPracticas?: string;
  descripcionAfiliados?: string;
  descripcionPrestadores?: string;
}

interface TipoArchivo {
  id: string;
  titulo: string;
  descripcion: string;
  icono: React.ReactNode;
  color: string;
  aceptados: string;
}

interface ArchivoImportacion {
  id: string;
  archivo: File;
  tipoArchivo: string;
  descripcion?: string;
  progreso: number;
  estado: 'pendiente' | 'subiendo' | 'completado' | 'error';
  mensaje?: string;
  errores?: any[];
}

const schema = yup.object().shape({
  descripcionPracticas: yup.string(),
  descripcionAfiliados: yup.string(),
  descripcionPrestadores: yup.string(),
});

const tiposArchivo: TipoArchivo[] = [
  {
    id: "practicas",
    titulo: "Precarga de Prácticas",
    descripcion: "Importar prácticas médicas de SEMPRE",
    icono: <MedicalServicesIcon sx={{ fontSize: 48 }} />,
    color: "#1976d2",
    aceptados: ".xlsx"
  },
  {
    id: "afiliados", 
    titulo: "Afiliados SEMPRE",
    descripcion: "Cargar datos de afiliados",
    icono: <GroupIcon sx={{ fontSize: 48 }} />,
    color: "#2e7d32",
    aceptados: ".txt"
  },
  {
    id: "prestadores",
    titulo: "Prestadores Médicos",
    descripcion: "Cargar listado de prestadores médicos",
    icono: <LocalHospitalIcon sx={{ fontSize: 48 }} />,
    color: "#ed6c02",
    aceptados: ".txt"
  }
];

export default function SempreImportacion() {
  const [archivos, setArchivos] = useState<ArchivoImportacion[]>([]);
  const [showAlertMessage, setShowAlertMessage] = useState(false);
  const [textAlertMessage, setTextAlertMessage] = useState("");
  const [modalErrores, setModalErrores] = useState<{ isOpen: boolean; errores: any[]; titulo: string }>({
    isOpen: false,
    errores: [],
    titulo: ''
  });

  const {
    control,
    watch,
    getFieldState,
    formState,
    formState: { errors },
  } = useForm<ImportacionForm>({
    mode: "all",
    resolver: yupResolver(schema),
    defaultValues: {
      descripcionPracticas: "",
      descripcionAfiliados: "",
      descripcionPrestadores: "",
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>, tipoArchivo: string) => {
    const files = event.target.files;
    
    if (!files || files.length === 0) return;

    // Solo tomar el primer archivo
    const file = files[0];

    // Obtener la descripción según el tipo
    const descripcionField = `descripcion${tipoArchivo.charAt(0).toUpperCase() + tipoArchivo.slice(1)}` as keyof ImportacionForm;
    const descripcion = watch(descripcionField);

    const nuevoArchivo: ArchivoImportacion = {
      id: `${Date.now()}`,
      archivo: file,
      tipoArchivo,
      descripcion: descripcion || `${tipoArchivo} - ${file.name}`,
      progreso: 0,
      estado: 'pendiente',
    };

    setArchivos(prev => [...prev, nuevoArchivo]);
    event.target.value = '';
  };

  const eliminarArchivo = (id: string) => {
    setArchivos(prev => prev.filter(archivo => archivo.id !== id));
  };

  // Método genérico que decide qué servicio usar
  const getImportService = (tipoArchivo: string) => {
    switch (tipoArchivo) {
      case 'practicas':
        return importacionSempreServices;
      case 'afiliados':
        return afiliadoSempreServices;
      case 'prestadores':
        return prestadorSempreServices;
      default:
        throw new Error(`Tipo de archivo no válido: ${tipoArchivo}`);
    }
  };

  // Verificar si hay algún archivo siendo procesado
  const hayArchivoProcesandose = () => {
    return archivos.some(a => a.estado === 'subiendo');
  };

  const subirArchivo = async (archivo: ArchivoImportacion) => {
    // Verificar si ya hay otro archivo procesándose
    if (hayArchivoProcesandose()) {
      const archivoEnProceso = archivos.find(a => a.estado === 'subiendo');
      setTextAlertMessage(`Ya hay un archivo siendo procesado (${archivoEnProceso?.archivo.name}). Espere a que termine antes de subir otro.`);
      setShowAlertMessage(true);
      return;
    }

    setArchivos(prev => prev.map(a => 
      a.id === archivo.id ? { ...a, estado: 'subiendo' } : a
    ));

    try {
      const formData = new FormData();
      formData.append('File', archivo.archivo);
      if (archivo.descripcion) {
        formData.append('descripcion', archivo.descripcion);
      }

      // Usar el servicio específico según el tipo de archivo
      const service = getImportService(archivo.tipoArchivo);
      const response = await service.importar(formData);
      
      // Manejar la respuesta del backend
      const uploadResult = response.data;
      let mensaje = `Archivo procesado correctamente. Filas cargadas: ${uploadResult.rowsUploadQuantity}`;
      let estadoFinal: ArchivoImportacion['estado'] = 'completado';
      
      if (uploadResult.tieneErrores && uploadResult.lineasConError?.length > 0) {
        mensaje += `. Se encontraron ${uploadResult.lineasConError.length} líneas con advertencias.`;
        // Como el HTTP status es 200, mantenemos como 'completado' pero con errores
      }
      
      setArchivos(prev => prev.map(a => 
        a.id === archivo.id 
          ? { 
              ...a, 
              progreso: 100, 
              estado: estadoFinal,
              mensaje: mensaje,
              // Guardar los errores para mostrar en el modal
              errores: uploadResult.lineasConError || []
            } 
          : a
      ));

    } catch (error: any) {
      let mensajeError = 'Error al importar el archivo';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          mensajeError = error.response.data;
        } else if (error.response.data.message) {
          mensajeError = error.response.data.message;
        } else if (error.response.data.lineasConError?.length > 0) {
          // Mostrar los primeros errores si existen
          const primerosErrores = error.response.data.lineasConError.slice(0, 3);
          mensajeError = `Errores encontrados: ${primerosErrores.map((e: any) => `Línea ${e.lineaNumero}: ${e.descripcion}`).join(', ')}`;
          if (error.response.data.lineasConError.length > 3) {
            mensajeError += ` y ${error.response.data.lineasConError.length - 3} errores más.`;
          }
        }
      }
      
      setArchivos(prev => prev.map(a => 
        a.id === archivo.id 
          ? { 
              ...a, 
              estado: 'error',
              progreso: 0,
              mensaje: mensajeError
            } 
          : a
      ));
    }
  };

  const limpiarCompletados = () => {
    setArchivos(prev => prev.filter(a => a.estado !== 'completado'));
  };

  const mostrarDetalleErrores = (archivo: ArchivoImportacion) => {
    if (archivo.errores && archivo.errores.length > 0) {
      setModalErrores({
        isOpen: true,
        errores: archivo.errores,
        titulo: `Errores en ${archivo.archivo.name}`
      });
    }
  };

  const cerrarModalErrores = () => {
    setModalErrores({
      isOpen: false,
      errores: [],
      titulo: ''
    });
  };

  const exportarErrores = () => {
    if (modalErrores.errores.length === 0) return;
    
    const csvContent = [
      'Línea,Error',
      ...modalErrores.errores.map(error => `${error.lineaNumero},"${error.descripcion}"`)
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `errores_${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const descargarPlantilla = async (tipoArchivo: string) => {
    try {
      // Usar el servicio específico para descargar plantilla
      const service = getImportService(tipoArchivo);
      const response = await service.descargarPlantilla();
      
      // Crear un blob y descargarlo
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `plantilla_${tipoArchivo}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      setTextAlertMessage(`Error al descargar la plantilla de ${tipoArchivo}`);
      setShowAlertMessage(true);
    }
  };

  useEffect(() => {
    if (showAlertMessage) {
      const timer = setTimeout(() => setShowAlertMessage(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showAlertMessage]);

  const getEstadoColor = (estado: ArchivoImportacion['estado'], archivo?: ArchivoImportacion) => {
    switch (estado) {
      case 'pendiente': return '#ffa726';
      case 'subiendo': return '#42a5f5';
      case 'completado': 
        return archivo?.errores && archivo.errores.length > 0 
          ? '#ff9800' // Naranja para advertencias
          : '#66bb6a'; // Verde para completado sin errores
      case 'error': return '#ef5350';
      default: return '#9e9e9e';
    }
  };

  const getEstadoTexto = (estado: ArchivoImportacion['estado'], archivo?: ArchivoImportacion) => {
    switch (estado) {
      case 'pendiente': return 'Pendiente';
      case 'subiendo': return 'Subiendo...';
      case 'completado': 
        return archivo?.errores && archivo.errores.length > 0 
          ? 'Completado con Advertencias' 
          : 'Completado';
      case 'error': return 'Error';
      default: return 'Desconocido';
    }
  };

  const getIconForFileType = (tipoArchivo: string) => {
    const tipo = tiposArchivo.find(t => t.id === tipoArchivo);
    if (tipo) {
      // Clonar el icono con tamaño reducido para el Avatar
      if (tipo.id === 'practicas') return <MedicalServicesIcon sx={{ fontSize: 24 }} />;
      if (tipo.id === 'afiliados') return <GroupIcon sx={{ fontSize: 24 }} />;
      if (tipo.id === 'prestadores') return <LocalHospitalIcon sx={{ fontSize: 24 }} />;
    }
    return <InsertDriveFileIcon sx={{ fontSize: 24 }} />;
  };

  return (
    <div className="sempre-importacion">
      <div className="header-section">
        <h1>Importación de Archivos SEMPRE</h1>
        <p>Seleccione uno o más tipos de archivos y cargue los datos para procesar de forma secuencial</p>
      </div>

      {showAlertMessage && (
        <Alert severity="warning" onClose={() => setShowAlertMessage(false)}>
          {textAlertMessage}
        </Alert>
      )}

      {/* Cards de selección de tipo de archivo */}
      <div className="tipos-archivo-grid">
        {tiposArchivo.map((tipo) => (
          <Card 
            key={tipo.id} 
            className="tipo-archivo-card" 
            sx={{ 
              borderColor: tipo.color,
              position: 'relative'
            }}
          >
            <CardContent>
              <div className="tipo-header">
                <div className="tipo-icon" style={{ color: tipo.color }}>
                  {tipo.icono}
                </div>
                <div className="tipo-info">
                  <Typography variant="h6" component="h3" sx={{ color: tipo.color, fontWeight: 600 }}>
                    {tipo.titulo}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {tipo.descripcion}
                  </Typography>
                </div>
              </div>

              {/* Campo de descripción específico para cada tipo */}
              <div className="description-field">
                <Controller
                  name={`descripcion${tipo.id.charAt(0).toUpperCase() + tipo.id.slice(1)}` as keyof ImportacionForm}
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      label="Descripción (Opcional)"
                      placeholder={`Descripción para ${tipo.titulo.toLowerCase()}...`}
                      getFieldState={getFieldState}
                      formState={formState}
                      errors={errors[`descripcion${tipo.id.charAt(0).toUpperCase() + tipo.id.slice(1)}` as keyof ImportacionForm]}
                      {...field}
                    />
                  )}
                />
              </div>

              <div className="tipo-actions">
                <input
                  id={`file-input-${tipo.id}`}
                  type="file"
                  accept={tipo.aceptados}
                  onChange={(e) => handleFileSelect(e, tipo.id)}
                  style={{ display: 'none' }}
                />
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => document.getElementById(`file-input-${tipo.id}`)?.click()}
                  fullWidth
                  sx={{ 
                    backgroundColor: tipo.color,
                    '&:hover': {
                      backgroundColor: tipo.color,
                      opacity: 0.9
                    }
                  }}
                >
                  Seleccionar Archivo
                </Button>
                
                {/* Botón de plantilla oculto por ahora - TODO: habilitar cuando estén las plantillas */}
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => descargarPlantilla(tipo.id)}
                  variant="outlined"
                  sx={{ 
                    mt: 1,
                    borderColor: tipo.color,
                    color: tipo.color,
                    '&:hover': {
                      borderColor: tipo.color,
                      backgroundColor: `${tipo.color}10`
                    },
                    display: 'none' // Oculto hasta que estén las plantillas
                  }}
                  fullWidth
                >
                  Descargar Plantilla
                </Button>
              </div>

              {/* Mostrar archivos de este tipo */}
              {archivos.filter(a => a.tipoArchivo === tipo.id).length > 0 && (
                <div className="archivos-tipo">
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: tipo.color }}>
                    Archivo seleccionado ({archivos.filter(a => a.tipoArchivo === tipo.id).length}):
                  </Typography>
                  <div className="archivos-mini-list">
                    {archivos.filter(a => a.tipoArchivo === tipo.id).map((archivo) => (
                      <div key={archivo.id} className="archivo-mini-item" style={{ borderLeftColor: tipo.color }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {archivo.archivo.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(archivo.archivo.size / 1024).toFixed(2)} KB
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ color: getEstadoColor(archivo.estado, archivo), fontWeight: 'bold' }}
                        >
                          {getEstadoTexto(archivo.estado, archivo)}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {archivos.length > 0 && (
        <Card className="files-card">
          <CardContent>
            <div className="files-header">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" component="h2">
                  {archivos.length === 1 ? 'Archivo para Importar' : `Archivos para Importar (${archivos.length})`}
                </Typography>
                {hayArchivoProcesandose() && (
                  <Chip 
                    label="🔄 Procesando"
                    size="small" 
                    sx={{ 
                      bgcolor: '#e3f2fd',
                      color: '#1976d2',
                      fontWeight: 600,
                      '@keyframes blink': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.5 },
                        '100%': { opacity: 1 }
                      },
                      animation: 'blink 1.5s infinite'
                    }}
                  />
                )}
              </Box>
              <div className="files-actions">
                <Button
                  variant="outlined"
                  startIcon={<DeleteOutlineOutlinedIcon />}
                  onClick={limpiarCompletados}
                  disabled={!archivos.some(a => a.estado === 'completado')}
                >
                  Limpiar Completados
                </Button>
              </div>
            </div>

            <div className="files-list">
              {archivos.map((archivo) => (
                <Card 
                  key={archivo.id} 
                  className="file-item" 
                  variant="outlined"
                  sx={{
                    borderColor: archivo.estado === 'subiendo' ? '#1976d2' : undefined,
                    borderWidth: archivo.estado === 'subiendo' ? 2 : 1,
                    backgroundColor: archivo.estado === 'subiendo' ? '#f3f7ff' : undefined,
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }
                  }}
                >
                  <CardContent>
                    <div className="file-info">
                      {/* Header con icono y nombre del archivo */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: tiposArchivo.find(t => t.id === archivo.tipoArchivo)?.color || '#1976d2',
                            width: 48,
                            height: 48,
                            mr: 2
                          }}
                        >
                          {getIconForFileType(archivo.tipoArchivo)}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {archivo.archivo.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Chip 
                              label={tiposArchivo.find(t => t.id === archivo.tipoArchivo)?.titulo}
                              size="small"
                              sx={{ 
                                bgcolor: `${tiposArchivo.find(t => t.id === archivo.tipoArchivo)?.color}15`,
                                color: tiposArchivo.find(t => t.id === archivo.tipoArchivo)?.color,
                                fontWeight: 500
                              }}
                            />
                            <Chip 
                              label={`${(archivo.archivo.size / 1024).toFixed(2)} KB`}
                              size="small"
                              variant="outlined"
                              icon={<DataUsageIcon />}
                            />
                            <Chip 
                              label={getEstadoTexto(archivo.estado, archivo)}
                              size="small"
                              sx={{ 
                                bgcolor: `${getEstadoColor(archivo.estado, archivo)}15`,
                                color: getEstadoColor(archivo.estado, archivo),
                                fontWeight: 600
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>

                      {/* Descripción si existe */}
                      {archivo.descripcion && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ 
                            bgcolor: '#f5f5f5', 
                            p: 1, 
                            borderRadius: 1,
                            fontStyle: 'italic'
                          }}>
                            📝 {archivo.descripcion}
                          </Typography>
                        </Box>
                      )}

                      {/* Indicador de procesamiento */}
                      {archivo.estado === 'subiendo' && (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mb: 2,
                          bgcolor: '#e3f2fd',
                          p: 1.5,
                          borderRadius: 1,
                          border: '1px solid #bbdefb',
                          '@keyframes pulse': {
                            '0%': { opacity: 1 },
                            '50%': { opacity: 0.7 },
                            '100%': { opacity: 1 }
                          },
                          animation: 'pulse 2s infinite'
                        }}>
                          <CircularProgress 
                            size={20} 
                            sx={{ mr: 1.5, color: '#1976d2' }} 
                          />
                          <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                            ⚡ Procesando archivo... Por favor espere.
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Mensaje de resultado */}
                      {archivo.mensaje && (
                        <Box sx={{ mb: 2 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: archivo.estado === 'error' ? 'error.main' : 'success.main',
                              bgcolor: archivo.estado === 'error' ? '#ffebee' : '#e8f5e8',
                              p: 1.5,
                              borderRadius: 1,
                              border: `1px solid ${archivo.estado === 'error' ? '#ffcdd2' : '#c8e6c9'}`,
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: 1
                            }}
                          >
                            <span>{archivo.estado === 'error' ? '❌' : '✅'}</span>
                            <span style={{ flexGrow: 1 }}>{archivo.mensaje}</span>
                            {archivo.errores && archivo.errores.length > 0 && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ErrorOutlineIcon />}
                                onClick={() => mostrarDetalleErrores(archivo)}
                                sx={{ 
                                  minWidth: 'auto', 
                                  borderColor: '#ff9800',
                                  color: '#ff9800',
                                  '&:hover': {
                                    borderColor: '#f57c00',
                                    bgcolor: '#fff3e0'
                                  }
                                }}
                              >
                                Ver Detalles
                              </Button>
                            )}
                          </Typography>
                        </Box>
                      )}

                      {/* Sección de acciones */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end', 
                        gap: 1, 
                        pt: 1,
                        borderTop: '1px solid #e0e0e0' 
                      }}>
                        {archivo.estado === 'pendiente' && (
                          <Tooltip 
                            title={hayArchivoProcesandose() ? "Hay otro archivo siendo procesado. Espere a que termine." : "Subir archivo"}
                            arrow
                          >
                            <span>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<CloudUploadIcon />}
                                onClick={() => subirArchivo(archivo)}
                                disabled={hayArchivoProcesandose()}
                                sx={{
                                  minWidth: 100,
                                  bgcolor: hayArchivoProcesandose() ? '#ccc' : tiposArchivo.find(t => t.id === archivo.tipoArchivo)?.color,
                                  '&:hover': {
                                    bgcolor: hayArchivoProcesandose() ? '#ccc' : tiposArchivo.find(t => t.id === archivo.tipoArchivo)?.color,
                                    opacity: 0.9
                                  }
                                }}
                              >
                                Subir
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        
                        <Tooltip title="Eliminar archivo" arrow>
                          <IconButton
                            size="small"
                            onClick={() => eliminarArchivo(archivo.id)}
                            disabled={archivo.estado === 'subiendo'}
                            color="error"
                            sx={{
                              '&:hover': {
                                bgcolor: '#ffebee'
                              }
                            }}
                          >
                            <DeleteOutlineOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="info-card">
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom>
            Información sobre Formatos de Archivo
          </Typography>
          
          <div className="format-info">
            <div className="format-item">
              <Typography variant="subtitle2">Precarga de Prácticas:</Typography>
              <Typography variant="body2">
                Excel (xlsx) con columnas.
              </Typography>
            </div>
            
            <div className="format-item">
              <Typography variant="subtitle2">Afiliados SEMPRE:</Typography>
              <Typography variant="body2">
                Formato txt
              </Typography>
            </div>
            
            <div className="format-item">
              <Typography variant="subtitle2">Prestadores:</Typography>
              <Typography variant="body2">
                Formato txt
              </Typography>
            </div>
          </div>

          <div className="important-notes">
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Notas Importantes:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Se pueden seleccionar archivos de múltiples tipos simultáneamente
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Solo se puede procesar un archivo a la vez (procesamiento secuencial)
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Los botones de subida se deshabilitan mientras hay un archivo procesándose
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Los archivos deben estar en los formatos indicados.
            </Typography>
            <Typography variant="body2">
              • Verificar que los datos no contengan caracteres especiales
            </Typography>
          </div>
        </CardContent>
      </Card>

      {/* Modal para mostrar detalles de errores */}
      <Dialog
        open={modalErrores.isOpen}
        onClose={cerrarModalErrores}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {modalErrores.titulo}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            El archivo se procesó correctamente, pero se encontraron {modalErrores.errores.length} líneas con advertencias o errores que requieren atención.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Línea</strong></TableCell>
                  <TableCell><strong>Error</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modalErrores.errores.map((error, index) => (
                  <TableRow key={index}>
                    <TableCell>{error.lineaNumero}</TableCell>
                    <TableCell>{error.descripcion}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={exportarErrores} 
            variant="outlined"
            startIcon={<DownloadIcon />}
          >
            Exportar Errores
          </Button>
          <Button onClick={cerrarModalErrores} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
